import { useEffect, useState } from "react";
import "./HeroVideoStage.css";

/**
 * Cinematic 6-scene loop that replaces the static dashboard mockup on the
 * landing hero. Mimics the institutional video script (risco invisível →
 * plataforma → questionário → dashboard → IA + plano → AEP) using pure CSS
 * keyframes — no <video> tag, no external assets, ~10 KB on the wire.
 */
const SCENES = [
  { overline: "Cena 01",   title: "Riscos invisíveis na operação." },
  { overline: "Cena 02",   title: "Conheça a NR01 Sollute." },
  { overline: "Cena 03",   title: "Aplicação em qualquer dispositivo." },
  { overline: "Cena 04",   title: "Inteligência em tempo real." },
  { overline: "Cena 05",   title: "IA cria seu plano de ação." },
  { overline: "Cena 06",   title: "Relatório AEP pronto para o MTE." },
];

// Each scene plays for this many ms before the next fades in.
const SCENE_DURATION = 5500;

export default function HeroVideoStage() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setActive((s) => (s + 1) % SCENES.length);
    }, SCENE_DURATION);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="hvs-stage" data-testid="hero-video-stage" aria-label="Demonstração animada da plataforma NR01 Sollute">
      <div className="hvs-brand-pill">
        <span className="hvs-brand-dot"></span> NR01 Sollute · Demo
      </div>

      {/* Particles + scan line overlay (always on) */}
      <div className="hvs-particles" aria-hidden="true">
        {Array.from({ length: 12 }).map((_, i) => (
          <span
            key={i}
            style={{
              left: `${(i * 8 + 4) % 96}%`,
              animationDelay: `${(i * 0.6) % 6}s`,
              animationDuration: `${5 + (i % 4)}s`,
            }}
          />
        ))}
      </div>
      <div className="hvs-scanline" aria-hidden="true"></div>

      {/* === SCENE 1: Risco invisível — 16 cells with red alert pulses === */}
      <div className={`hvs-scene ${active === 0 ? "is-active" : ""}`} aria-hidden={active !== 0}>
        <div className="hvs-s1-grid">
          {Array.from({ length: 16 }).map((_, i) => (
            <div key={i} className={`hvs-s1-cell ${[1, 5, 9, 12].includes(i) ? "alert" : ""}`}></div>
          ))}
        </div>
      </div>

      {/* === SCENE 2: Plataforma surge — logo orb com órbita === */}
      <div className={`hvs-scene ${active === 1 ? "is-active" : ""}`} aria-hidden={active !== 1}>
        <div className="hvs-s2-ring">
          <div className="hvs-s2-logo">S</div>
          <span className="hvs-s2-orbit"></span>
          <span className="hvs-s2-orbit b"></span>
        </div>
      </div>

      {/* === SCENE 3: Questionário no celular === */}
      <div className={`hvs-scene ${active === 2 ? "is-active" : ""}`} aria-hidden={active !== 2}>
        <div className="hvs-s3-phone">
          <div style={{ color: "#8BC53F", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>
            Questionário NR-01 · 32/54
          </div>
          <div className="hvs-s3-progress"><div></div></div>
          <div className="hvs-s3-q">O ritmo de trabalho é intenso durante a maior parte da jornada?</div>
          <div className="hvs-s3-opt">Nunca</div>
          <div className="hvs-s3-opt">Raramente</div>
          <div className="hvs-s3-opt sel">Às vezes</div>
          <div className="hvs-s3-opt">Frequentemente</div>
          <div className="hvs-s3-opt">Sempre</div>
        </div>
      </div>

      {/* === SCENE 4: Dashboard executivo === */}
      <div className={`hvs-scene ${active === 3 ? "is-active" : ""}`} aria-hidden={active !== 3}>
        <div className="hvs-s4" key={active === 3 ? "live" : "static"}>
          <div className="hvs-s4-head">
            <div>
              <div className="label">Risco médio · em tempo real</div>
              <div style={{ color: "white", fontSize: 13, fontWeight: 700, marginTop: 4 }}>Empresa Beta · GHE-04</div>
            </div>
            <div className="num">54<span style={{ fontSize: 12, opacity: 0.6 }}>%</span></div>
          </div>
          {[
            ["Demandas",     78, "linear-gradient(90deg,#dc2626,#f87171)"],
            ["Controle",     62, "linear-gradient(90deg,#ea580c,#fb923c)"],
            ["Liderança",    45, "linear-gradient(90deg,#eab308,#facc15)"],
            ["Relações",     31, "linear-gradient(90deg,#22c55e,#86efac)"],
            ["Ambiente",     57, "linear-gradient(90deg,#ea580c,#fb923c)"],
          ].map(([name, val, bg]) => (
            <div className="hvs-s4-row" key={name}>
              <span className="name">{name}</span>
              <div className="bar"><div style={{ width: `${val}%`, background: bg }}></div></div>
              <span className="val">{val}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* === SCENE 5: IA — rede neural com nodes/conexões === */}
      <div className={`hvs-scene ${active === 4 ? "is-active" : ""}`} aria-hidden={active !== 4}>
        <div className="hvs-s5-net">
          <svg className="hvs-s5-svg" viewBox="0 0 320 240" fill="none" preserveAspectRatio="none">
            <path d="M40,40 L160,120 L40,200" />
            <path d="M280,40 L160,120 L280,200" />
            <path d="M160,40 L160,200" />
            <path d="M40,120 L280,120" />
          </svg>
          <span className="hvs-s5-node big" style={{ top: "calc(50% - 14px)", left: "calc(50% - 14px)" }}></span>
          <span className="hvs-s5-node" style={{ top: "16%", left: "10%" }}></span>
          <span className="hvs-s5-node" style={{ top: "16%", right: "10%" }}></span>
          <span className="hvs-s5-node" style={{ bottom: "16%", left: "10%" }}></span>
          <span className="hvs-s5-node" style={{ bottom: "16%", right: "10%" }}></span>
          <span className="hvs-s5-node" style={{ top: "10%", left: "50%", marginLeft: "-7px" }}></span>
          <span className="hvs-s5-node" style={{ bottom: "10%", left: "50%", marginLeft: "-7px" }}></span>
        </div>
      </div>

      {/* === SCENE 6: Folha do AEP === */}
      <div className={`hvs-scene ${active === 5 ? "is-active" : ""}`} aria-hidden={active !== 5}>
        <div className="hvs-s6-paper" key={active === 5 ? "paper-live" : "paper-static"}>
          <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 8, color: "#94a3b8", letterSpacing: "0.18em", textTransform: "uppercase", paddingTop: 8 }}>
            Relatório AEP · NR-01
          </div>
          <div className="ttl">Avaliação dos Fatores Psicossociais</div>
          <div className="lines">
            <span></span><span></span><span></span><span></span><span></span>
          </div>
          <div className="badge">Conforme MTE</div>
        </div>
      </div>

      {/* Bottom caption */}
      <div className="hvs-caption" data-testid={`hvs-caption-${active}`}>
        <span className="overline">{SCENES[active].overline}</span>
        <h3>{SCENES[active].title}</h3>
      </div>

      {/* Scene indicators */}
      <div className="hvs-dots" aria-hidden="true">
        {SCENES.map((_, i) => (
          <span key={i} className={i === active ? "active" : ""}></span>
        ))}
      </div>
    </div>
  );
}
